import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

/**
 * Reflects the backend `TenantSecurityConfig` shape.
 *
 * BigInt fields (`pinMinValuePix`, `pinMinValueBoleto`) are serialized by the
 * backend as strings to avoid JSON precision loss — both are expressed in
 * centavos (cents).
 */
export interface TenantSecurityConfig {
  id: string;
  tenantId: string;

  // Master switches
  pinEnabled: boolean;
  rateLimitEnabled: boolean;
  loginLockEnabled: boolean;
  passwordPolicyEnabled: boolean;
  newDeviceDetectionEnabled: boolean;
  transactionAuditEnabled: boolean;
  transactionNotificationEnabled: boolean;

  // PIN config — BigInt serialized as string
  pinMinValuePix: string;
  pinMinValueBoleto: string;
  pinMaxAttempts: number;
  pinLockMinutes: number;

  // Rate limit (PIX)
  pixMaxAttempts: number;
  pixAttemptsWindowMinutes: number;
  pixBlockDurationMinutes: number;
  pixCooldownSeconds: number;

  // Login lock
  loginMaxAttempts: number;
  loginLockMinutes: number;
  loginPermanentLockAfter: number;

  // Password policy
  passwordMinLength: number;
  passwordRequireUppercase: boolean;
  passwordRequireLowercase: boolean;
  passwordRequireNumber: boolean;
  passwordRequireSpecial: boolean;
  passwordHistorySize: number;
  passwordForceResetOnWeak: boolean;

  // New device
  newDeviceEmailRequired: boolean;
  newDeviceBlockUntilConfirmed: boolean;

  createdAt: string;
  updatedAt: string;
}

/**
 * Payload for PUT /admin/security-config. All fields are optional —
 * only the ones informed are updated. BigInt fields are sent as number
 * (cents) and converted to BigInt on the backend.
 */
export interface UpdateSecurityConfigPayload {
  // Master switches
  pinEnabled?: boolean;
  rateLimitEnabled?: boolean;
  loginLockEnabled?: boolean;
  passwordPolicyEnabled?: boolean;
  newDeviceDetectionEnabled?: boolean;
  transactionAuditEnabled?: boolean;
  transactionNotificationEnabled?: boolean;

  // PIN config — sent as number (centavos)
  pinMinValuePix?: number;
  pinMinValueBoleto?: number;
  pinMaxAttempts?: number;
  pinLockMinutes?: number;

  // Rate limit (PIX)
  pixMaxAttempts?: number;
  pixAttemptsWindowMinutes?: number;
  pixBlockDurationMinutes?: number;
  pixCooldownSeconds?: number;

  // Login lock
  loginMaxAttempts?: number;
  loginLockMinutes?: number;
  loginPermanentLockAfter?: number;

  // Password policy
  passwordMinLength?: number;
  passwordRequireUppercase?: boolean;
  passwordRequireLowercase?: boolean;
  passwordRequireNumber?: boolean;
  passwordRequireSpecial?: boolean;
  passwordHistorySize?: number;
  passwordForceResetOnWeak?: boolean;

  // New device
  newDeviceEmailRequired?: boolean;
  newDeviceBlockUntilConfirmed?: boolean;
}

const SECURITY_CONFIG_QUERY_KEY = ['security-config'] as const;

export function useSecurityConfig() {
  return useQuery({
    queryKey: SECURITY_CONFIG_QUERY_KEY,
    queryFn: async () => {
      const { data } = await api.get<TenantSecurityConfig>('/admin/security-config');
      return data;
    },
  });
}

export function useUpdateSecurityConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateSecurityConfigPayload) => {
      const { data } = await api.put<TenantSecurityConfig>(
        '/admin/security-config',
        payload,
      );
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(SECURITY_CONFIG_QUERY_KEY, data);
      queryClient.invalidateQueries({ queryKey: SECURITY_CONFIG_QUERY_KEY });
    },
  });
}

export function useResetSecurityConfigDefaults() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<TenantSecurityConfig>(
        '/admin/security-config/reset-defaults',
      );
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(SECURITY_CONFIG_QUERY_KEY, data);
      queryClient.invalidateQueries({ queryKey: SECURITY_CONFIG_QUERY_KEY });
    },
  });
}
